import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { IconExternalLink } from '@lightspeed/flame/Icon/ExternalLink';
import { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { createReactRenderer } from 'root/apps/helpers/react-renderer';
import useLocalizedLink from 'root/apps/hooks/useLocalizedLink';
import {
  StyledBold,
  StyledContainer,
  StyledImage,
  StyledModal,
  StyledText,
} from 'root/apps/NuorderIntegrationModal/Modal.style';
import { fetchNuorderBaseUrl } from 'root/apps/pages/views/inventory/Vendor/b2b/utils/NuorderUtilities';

interface NewNuorderIntegrationModalProps {
  callback?: (result: boolean) => void;
  unmount?: () => void;
}

const NewNuorderIntegrationModal: React.FC<NewNuorderIntegrationModalProps> = ({
  callback,
  unmount,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const NUORDER_HELP_CENTRE_URL = `https://retail-support.lightspeedhq.com/hc/{{locale}}/articles/38633631918363-Placing-integrated-purchase-orders-with-Retail-POS-R-Series-and-NuORDER`;

  const localizedHelpCentreUrl = useLocalizedLink(NUORDER_HELP_CENTRE_URL);

  const baseUrl = fetchNuorderBaseUrl();

  const savePreference = () => {
    window.lsCloud.UserPreferences.save('ui_tab', 'inventory', 'nuorder.modal', 'hide');
  };

  const handleContinue = React.useCallback(() => {
    setIsOpen(false);
    savePreference();
    window.PageRouter.goTo('/inventory/vendors/b2b');
    if (typeof callback === 'function') callback(false);
    if (typeof unmount === 'function') unmount();
  }, [callback, unmount]);

  const handleNuorder = async () => {
    setIsOpen(false);
    savePreference();
    window.merchantos.utils.openUrl(await baseUrl, '_blank');
    if (typeof callback === 'function') callback(false);
    if (typeof unmount === 'function') unmount();
  };

  const handleClose = () => {
    setIsOpen(false);
    savePreference();
    if (typeof callback === 'function') callback(false);
    if (typeof unmount === 'function') unmount();
  };

  React.useEffect(() => {
    window.lsCloud.UserPreferences.get('ui_tab', 'inventory', 'nuorder.modal').then((value) => {
      const isSuppressed = value === 'hide';
      setIsOpen(!isSuppressed);
    });
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isOpen) {
        e.preventDefault();
        handleContinue();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleContinue]);

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleClose} shouldCloseOnOverlayClick={false}>
      <ModalHeader data-test="nuorder-integration-modal-header">
        <Text>Link your vendors with NuORDER for seamless purchase orders</Text>
      </ModalHeader>
      <ModalBody>
        <Flex justifyContent="space-between" mb={5}>
          <StyledContainer>
            <StyledImage alt="link vendors" src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/nuorder/link_vendors.svg" />
            <StyledText>1. Link your Vendors</StyledText>
            <Text>
              Link your NuORDER vendors to your account vendors from{' '}
              <StyledBold>Inventory &gt; NuORDER Vendors.</StyledBold>
            </Text>
          </StyledContainer>
          <StyledContainer>
            <StyledImage alt="create po" src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/nuorder/create_po.svg" />
            <StyledText>2. Create a Purchase Order</StyledText>
            <Text>
              Create a PO with a linked NuORDER vendor from{' '}
              <StyledBold>Inventory &gt; Purchase Orders.</StyledBold>
            </Text>
          </StyledContainer>
          <StyledContainer>
            <StyledImage alt="finalize and send" src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/nuorder/finalize_send.svg" />
            <StyledText>3. Finalize and send</StyledText>
            <Text>
              Finalize your PO and NuORDER can send it directly to your vendor from the POS.
            </Text>
          </StyledContainer>
        </Flex>
        <TextLink
          fontWeight={600}
          href={localizedHelpCentreUrl}
          target="_blank"
          data-test="nuorder-integration-read-more-link"
        >
          Read more about the NuORDER integration{' '}
          <IconExternalLink color={theme.textStyles.link.color} />
        </TextLink>
      </ModalBody>
      <ModalFooter>
        <Flex justifyContent="end">
          <Button mr={2} onClick={handleNuorder} data-test="nuorder-integration-nuorder-site-btn">
            Go to NuORDER
          </Button>
          <Button
            fill
            variant="secondary"
            onClick={handleContinue}
            data-test="nuorder-integration-link-vendors-btn"
          >
            Continue to Vendors
          </Button>
        </Flex>
      </ModalFooter>
    </StyledModal>
  );
};

export { NewNuorderIntegrationModal };

interface SetNewNuorderIntegrationModalProps {
  callback?: NewNuorderIntegrationModalProps['callback'];
}

export const setNewNuorderIntegrationModal = (props: SetNewNuorderIntegrationModalProps) => {
  const RenderedComponent = createReactRenderer({
    component: NewNuorderIntegrationModal,
    selector: '[data-react-root="ReactModal"]',
  });

  const finalProps = { ...props, unmount: RenderedComponent.unmount };
  RenderedComponent.mount({ props: finalProps });
};
